import Keycloak, { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js';
import { environment } from 'src/environments/environment';

/**
 * Corporate identity provider (OIDC) singleton.
 *
 * Provider-agnostic naming per the concealment policy: no `keycloak*` env vars, files
 * named `sso.ts`/`auth-sync.ts`, localStorage keys `token`/`role`/`hotelId`/`accountId`/
 * `auth_method`.
 *
 * Lifecycle:
 *  - `bootstrap()` runs once at app start (APP_INITIALIZER). It loads the SDK with
 *    `check-sso` (NEVER `login-required`) so the local-login path still works for
 *    users that aren't migrated yet. If `environment.auth.url` is empty, this is a
 *    no-op and the rest of the app is unaffected.
 *  - `login()` redirects the browser to the portal sign-in flow (PKCE S256).
 *  - `logout()` revokes the session and lands back on the app root.
 *
 * The instance is intentionally module-scoped (not an Angular service) so the HTTP
 * interceptor and the auth service can read the current token without DI plumbing.
 */

let instance: Keycloak | undefined;
let ready = false;
let enabledFlag = false;

function buildConfig(): KeycloakConfig | undefined {
  const auth = environment.auth;
  if (!auth?.url || !auth.realm || !auth.clientId) return undefined;
  return { url: auth.url, realm: auth.realm, clientId: auth.clientId };
}

export function ssoEnabled(): boolean {
  return enabledFlag;
}

export function ssoInstance(): Keycloak | undefined {
  return enabledFlag && ready ? instance : undefined;
}

export async function bootstrap(): Promise<void> {
  const config = buildConfig();
  if (!config) {
    enabledFlag = false;
    return;
  }
  enabledFlag = true;
  instance = new Keycloak(config);

  const initOptions: KeycloakInitOptions = {
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    // No silentCheckSsoRedirectUri: the silent check (prompt=none) runs via a
    // redirect instead of a hidden iframe. The auth server sends
    // `X-Frame-Options: sameorigin`, which blocks the iframe and hangs init()
    // (the app never leaves the "Loading ..." splash). Redirect is also
    // future-proof as browsers phase out the third-party cookies iframe SSO
    // relies on.
    checkLoginIframe: false,
  };

  try {
    await instance.init(initOptions);
    ready = true;
  } catch (err) {
    // Fail open: the local-login path remains usable. Do not log the provider name.
    console.error('Identity provider init failed');
    ready = false;
  }
}

export function login(redirectPath: string = '/'): void {
  if (!instance) return;
  void instance.login({
    redirectUri: window.location.origin + redirectPath,
  });
}

export function logout(): void {
  if (!instance) return;
  void instance.logout({
    redirectUri: window.location.origin + '/auth/login',
  });
}

export async function refresh(minValiditySeconds: number = 30): Promise<string | undefined> {
  if (!instance || !ready) return undefined;
  try {
    await instance.updateToken(minValiditySeconds);
    return instance.token;
  } catch {
    return undefined;
  }
}
